import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Grid, Row, Col } from 'react-bootstrap';
import { get } from 'lodash';
import ContentTile from 'components/atoms/layout/ContentTile';
import SparkLineContainer from 'containers/charts/SparkLineContainer';
import ChannelCalloutMetric from 'components/molecules/ChannelCalloutMetric';
import { ExportableChannelSummaryCallout } from 'containers/callouts/Exportable';
import { getDefaultMetricMap, getCalloutMap, createMetricMap, createCalloutValues } from 'helpers/callouts';
import { getActivitiesSettings } from 'helpers/objectiveSettings';

import {
    SparkLineWrapper,
    CategoriesWrapper,
    SparklineTitleWrapper
} from 'components/atoms/callout/SparkLine';

import breakpoint from 'styled-components-breakpoint';

const CalloutContentTile = styled(ContentTile)`
    margin-bottom: 20px;
`;
const CalloutGrid = styled(Grid)`
    padding-left: ${props => (props.disableDiveInLink ? '0px !important' : 'auto')};
`;
const CalloutLink = styled(Link)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    background-color: ${props => props.theme.channelCallouts.colors.linkBackground};
    transition: all ease 0.8s;
    border-left: 1px solid ${props => props.theme.channelCallouts.colors.linkBorder};

    :hover {
        background-color: ${props => props.theme.channelCallouts.colors.linkHover};
        text-decoration: none;
    }

    i {
        font-size: 20px;
        color: ${props => props.theme.channelCallouts.colors.linkArrow};

        ${breakpoint('xs', 'sm')`
            font-size: 15px;
        `}
    }

    span {
        color: ${props => props.theme.channelCallouts.colors.linkText};
        font-size: 10px;
        ${breakpoint('lg')`
            font-size: 12px;
        `}
        ${breakpoint('xs', 'md')`
            display: none;
        `}
    }
`;

const ChannelName = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: ${props => props.theme.channelCallouts.colors.channelNameBackground};
    color: ${props => props.theme.channelCallouts.colors.channelName};
    height: 100%;
    width: 100%;
    text-align: center;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;

    i {
        font-size: 24px;

        ${breakpoint('xs', 'sm')`
            font-size: 18px;
        `}
    }

    span {
        font-size: 10px;
        ${breakpoint('lg')`
            font-size: 14px;
        `}
        ${breakpoint('xs', 'md')`
            font-size: 9px;
        `}
    }
`;

const NoPaddingCol = styled(Col)`
    padding-left: 0;
    padding-right: 0;
    height: 100%;
`;

const CalloutCol = styled(Col)`
    ::after{
        background: ${props => props.theme.channelCallouts.colors.divider};
        width: 1px;
        content: "";
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
    }
`;

const CalloutRow = styled(Row)`
    height: 80px;
    display: flex;
    margin-left: ${props => (props.disableDiveInLink ? '0px' : '-15px')};
    align-items: center;

    div:nth-child(4){
            ::after{
                content: none;
        }
    }

    ${breakpoint('xs', 'sm')`
        height: 50px;
    `}

`;

class ChannelSummaryCallout extends PureComponent {
    render() {
        const { channel, callouts, entityId, channelPlatform, disableDiveInLink, channelOptions } = this.props;
        let { entityObjective, entityObjectiveLabelCode } = this.props;
        const channelUrl = channel.path.replace('{entityId}', entityId);

        // work around so we can leave labelcode undefined in the API and elsewhere when it is actually undefined
        if (!entityObjectiveLabelCode || entityObjectiveLabelCode === '') {
            entityObjectiveLabelCode = 'default';
        }

        // ///////////////////////////////////////////////////////////////////
        // TEMPORARY OVERRIDE TO ALLOW DASHBOARD TO WORK if eKPI is SELECTED
        // ///////////////////////////////////////////////////////////////////
        if (entityObjective === 'ekpi') {
            entityObjective = 'ecpa';
        }
        // ///////////////////////////////////////////////////////////////////

        const calloutData = callouts.data;
        const sparklineData = calloutData.sparkline;
        const categories = sparklineData ?
            sparklineData.categories.map((d, index) => <span key={index}>{d}</span>) : [];

        let channelCalloutMetrics;
        let sparkLineMetric;

        const metricMap = createMetricMap({
            entityObjective,
            entityObjectiveLabelCode,
            getActivitiesSettings,
            initialMetricMap: getDefaultMetricMap()
        });
        const calloutMap = getCalloutMap();
        // DCM Audio is a very special case at this point

        if (channel.channel === 'audio' && channelPlatform === 'dcm' && entityObjective === 'ecpa') {
            channelCalloutMetrics = ['impressions', 'ecpa_activities'];
            sparkLineMetric = metricMap[entityObjectiveLabelCode].impressions;
        } else if (channel.channel === 'social' && channelOptions && channelOptions.social.showSocialCpconv) {
            // Social-specific logic for showing cost/conv based on flag
            channelCalloutMetrics = calloutMap[entityObjective][channel.channel] || [];
            channelCalloutMetrics.splice(-1, 1, 'cost_per_conversion');
            sparkLineMetric = metricMap[entityObjectiveLabelCode][channelCalloutMetrics[channelCalloutMetrics.length - 1] || 'impressions'];
        } else {
            channelCalloutMetrics = calloutMap[entityObjective][channel.channel] || [];
            sparkLineMetric = metricMap[entityObjectiveLabelCode][channelCalloutMetrics[channelCalloutMetrics.length - 1] || 'impressions'];
        }


        const calloutsToDisplay = channelCalloutMetrics.map(metric =>
            createCalloutValues({ calloutData, ...metricMap[entityObjectiveLabelCode][metric] }));

        // Add in blank callouts up to 3
        while (calloutsToDisplay.length < 3) {
            calloutsToDisplay.push({ metric: 'noop' });
        }

        return (
            <CalloutContentTile>
                <CalloutGrid disableDiveInLink={disableDiveInLink} fluid>
                    <CalloutRow disableDiveInLink={disableDiveInLink}>
                        <NoPaddingCol xs={2} lg={1}>
                            <ChannelName>
                                <i className={channel.icon} />
                                <span>{channel.name}</span>
                            </ChannelName>
                        </NoPaddingCol>

                        {calloutsToDisplay.map(callout =>
                            <CalloutCol xs={3} lg={2} key={callout.metric}>
                                <ChannelCalloutMetric {...callout} />
                            </CalloutCol>
                        )}

                        <Col md={4} xsHidden smHidden mdHidden>
                            {sparklineData && sparkLineMetric ?
                                <React.Fragment>
                                    <SparkLineWrapper>
                                        <SparkLineContainer
                                            animateDuration={2000}
                                            data={sparklineData.data}
                                            y={sparkLineMetric.metric}
                                            fill={'#BCBCBC'}
                                            exportName={`${this.props.exportName}-sparkline`} />
                                    </SparkLineWrapper>
                                    <CategoriesWrapper>
                                        {categories}
                                    </CategoriesWrapper>
                                    <SparklineTitleWrapper>
                                        {metricMap[entityObjectiveLabelCode][sparkLineMetric.metric].title}
                                    </SparklineTitleWrapper>
                                </React.Fragment>
                                : null}
                        </Col>
                        {
                            !this.props.disableDiveInLink &&
                            <NoPaddingCol xs={1}>
                                <CalloutLink to={channelUrl} title="Go to Channel">
                                    <i className="icon-arrow-right" />
                                    <span>DIVE IN!</span>
                                </CalloutLink>
                            </NoPaddingCol>
                        }
                    </CalloutRow>
                </CalloutGrid>
                <ExportableChannelSummaryCallout
                    exportName={this.props.exportName}
                    channelName={channel.name}
                    channelType={channel.channel}
                    callouts={calloutsToDisplay}
                    categories={get(sparklineData, 'categories', [])}
                />
            </CalloutContentTile>
        );
    }
}

ChannelSummaryCallout.propTypes = {
    exportName: PropTypes.string,
    channel: PropTypes.object.isRequired,
    callouts: PropTypes.object,
    entityId: PropTypes.number,
    disableDiveInLink: PropTypes.bool,
    entityObjective: PropTypes.oneOf(['ecpa', 'ctr', 'ekpi']).isRequired
};

export default ChannelSummaryCallout;



// WEBPACK FOOTER //
// ./src/components/molecules/ChannelSummaryCallout.js